/**
 * @fileoverview Zod schema for User Management's "Add New User" form.
 * @description The finalized Add User form's fields (First Name, Last Name, Email, Phone, Role,
 * Department, Reporting Manager) as sent to POST /identity/invitations, which creates the user and
 * emails them the invitation. The backend needs a first and a last name of at most 100 characters
 * each. Whether a Reporting Manager is required depends on the chosen role, so the schema is built
 * per form with that rule. The State is optional: blank means All India.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { z } from 'zod'

import { emailField, mobileNumberField } from '@/shared/lib/validation/contact-fields.schema'

/** The backend's longest first or last name. */
const NAME_MAX_LENGTH = 100

/**
 * @description Builds the Add User form schema.
 * @param {(roleId: string) => boolean} requiresManager - Whether a role needs a reporting manager.
 * @returns {z.ZodType} The schema.
 */
export function createAddUserFormSchema(requiresManager: (roleId: string) => boolean) {
  return z
    .object({
      firstName: z
        .string()
        .trim()
        .min(1, 'sales.userManagement.addUserForm.errors.firstNameRequired')
        .max(NAME_MAX_LENGTH, 'sales.userManagement.addUserForm.errors.nameTooLong'),
      lastName: z
        .string()
        .trim()
        .min(1, 'sales.userManagement.addUserForm.errors.lastNameRequired')
        .max(NAME_MAX_LENGTH, 'sales.userManagement.addUserForm.errors.nameTooLong'),
      email: emailField({
        required: 'sales.userManagement.addUserForm.errors.emailRequired',
        invalid: 'sales.userManagement.addUserForm.errors.emailInvalid',
      }),
      phone: mobileNumberField(),
      roleId: z.string().min(1, 'sales.userManagement.addUserForm.errors.roleRequired'),
      departmentId: z.string().min(1, 'sales.userManagement.addUserForm.errors.deptRequired'),
      reportsTo: z.string().trim().optional(),
      // A state id, or '' for All India.
      stateId: z.string(),
    })
    .refine((data) => !requiresManager(data.roleId) || Boolean(data.reportsTo), {
      message: 'sales.userManagement.addUserForm.errors.reportsToRequired',
      path: ['reportsTo'],
    })
}

export type AddUserFormValues = z.infer<ReturnType<typeof createAddUserFormSchema>>
